/**
 * "Outline this project with AI" (D223): the lightbulb a task's breakdown uses, on a project.
 * Where a task becomes tiny steps to get started, a project becomes a handful of larger tasks -
 * an outline to work from. Says what it added, and offers to take it back; the tasks themselves
 * show up wherever tasks do. Absent where there is no AI to ask.
 */

import { useState } from "react";
import { useAiTierAvailability } from "../aiTiers";
import { ApiError, api, type Task } from "../api";
import { postNotice } from "../notices";
import { AiBreakdownButton } from "./AiBreakdownButton";

export function ProjectOutlineButton({
  projectId,
  onAdded,
}: {
  projectId: string;
  /** Called after tasks were added or taken back, so what shows tasks can refetch. */
  onAdded?: () => void;
}) {
  const tiers = useAiTierAvailability();
  const [added, setAdded] = useState<Task[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  if (!tiers.aiEnabled) return null;

  async function undo() {
    if (!added) return;
    try {
      const { removed, kept } = await api.undoProjectBreakdown(
        projectId,
        added.map((task) => task.id),
      );
      setAdded(null);
      setNote(
        `Took back ${removed.length} task${removed.length === 1 ? "" : "s"}` +
          (kept.length ? `; kept ${kept.length} you had already worked on.` : "."),
      );
      onAdded?.();
    } catch (err) {
      postNotice(err instanceof ApiError ? err.message : "Couldn't take that back.");
    }
  }

  return (
    <span className="row wrap" style={{ gap: "0.4rem", alignItems: "center" }}>
      <AiBreakdownButton
        task={projectId}
        tiers={tiers}
        onRun={async (id, tier) => {
          const { tasks } = await api.aiProjectBreakdown(id, tier);
          setAdded(tasks);
          setNote(null);
          onAdded?.();
        }}
      />
      <span className="small muted" role="status">
        {added
          ? `Added ${added.length} tasks: ${added.map((task) => task.title).join(", ")}.`
          : (note ?? "Outline it into tasks with AI")}
      </span>
      {added && (
        <button type="button" className="btn ghost small" onClick={() => void undo()}>
          Undo
        </button>
      )}
    </span>
  );
}
